/**
 * Procurement — TanStack Query hooks. Repointed to the `/api` surface
 * (#1151 PR-4): the service now resolves unwrapped payloads (ADR-0006
 * §5 envelope stripped by `http`), so the hooks carry the inner shapes
 * directly — no `ApiResponse<T>` wrapper.
 *
 * The rebuilt backend splits procurement into two resources. Suggestions
 * carry an accept (→ PO) / dismiss lifecycle; purchase orders carry
 * submit / cancel / close plus the receive flow. Invalidation is scoped
 * to what each mutation actually touches: dismiss/create/transition/receipt
 * sweep only their own resource prefix (suggestions vs purchase-orders,
 * plus the specific PO detail on PO-scoped writes); accept crosses both
 * resources (consumes a suggestion AND creates a PO), so it alone sweeps
 * the `all()` prefix. See each hook's `onSuccess` for the exact keys.
 *
 * Stale-time choices:
 *   - suggestions: 30s (operator-actionable queue; short window so an
 *     "accept / dismiss → refresh" loop feels live).
 *   - purchase-orders list: 60s (PO state changes operator-driven).
 *   - purchase-order detail: 30s (receive page reads from this; bounded
 *     so it sees fresh state after a status transition).
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { queryKeys } from '@lib/queryKeys';
import type { ListPage } from '@/services/list-envelope';
import {
  procurementAPI,
  type PurchaseSuggestion,
  type PurchaseOrder,
  type PurchaseOrderDetail,
  type SuggestionListParams,
  type AcceptSuggestionInput,
  type CreatePurchaseOrderInput,
  type RecordReceiptInput,
} from '@/services';

const SUGGESTIONS_STALE_MS = 30_000;
const PO_DETAIL_STALE_MS = 30_000;

export function useSuggestions(params: SuggestionListParams = {}) {
  return useQuery<ListPage<PurchaseSuggestion>>({
    queryKey: queryKeys.procurement.suggestionsList(params),
    queryFn: () => procurementAPI.listSuggestions(params),
    staleTime: SUGGESTIONS_STALE_MS,
  });
}

export function usePurchaseOrderDetail(id: string | undefined) {
  return useQuery<PurchaseOrderDetail>({
    queryKey: queryKeys.procurement.purchaseOrderDetail(id),
    queryFn: () => {
      if (!id) {
        // Defensive — guarded by `enabled` below. If we got here, the
        // caller wired the hook without an id check; fail-loud per
        // EQD §3 so the bug surfaces immediately rather than via a
        // silently-empty render.
        throw new Error('usePurchaseOrderDetail: id is required');
      }
      return procurementAPI.getPurchaseOrder(id);
    },
    enabled: Boolean(id),
    staleTime: PO_DETAIL_STALE_MS,
  });
}

export function useAcceptSuggestion() {
  const qc = useQueryClient();
  return useMutation<PurchaseOrder, Error, { id: string; input: AcceptSuggestionInput }>({
    mutationFn: ({ id, input }) => procurementAPI.acceptSuggestion(id, input),
    onSuccess: () => {
      // Accept both consumes the suggestion and adds a PO row.
      qc.invalidateQueries({ queryKey: queryKeys.procurement.all() });
    },
  });
}

export function useDismissSuggestion() {
  const qc = useQueryClient();
  return useMutation<PurchaseSuggestion, Error, string>({
    mutationFn: (id) => procurementAPI.dismissSuggestion(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.procurement.suggestions() });
    },
  });
}

export function useCreatePurchaseOrder() {
  const qc = useQueryClient();
  return useMutation<PurchaseOrder, Error, CreatePurchaseOrderInput>({
    mutationFn: (input) => procurementAPI.createPurchaseOrder(input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.procurement.purchaseOrders() });
    },
  });
}

/** PO lifecycle transitions (submit / cancel / close): the three
 *  no-body status changes, one API call each. Declared as an `as const`
 *  map so the action union is derived from a single source
 *  (`keyof typeof`) rather than hand-written in both the type param and
 *  a switch — per the project's "magic-string cross-component contract"
 *  ban (must derive the union, not repeat it). */
const PO_TRANSITIONS = {
  submit: (id: string) => procurementAPI.submitPurchaseOrder(id),
  cancel: (id: string) => procurementAPI.cancelPurchaseOrder(id),
  close: (id: string) => procurementAPI.closePurchaseOrder(id),
} as const;

export type PurchaseOrderTransitionAction = keyof typeof PO_TRANSITIONS;

/** Every transition changes PO status, so both the list and this PO's
 *  detail are invalidated. */
export function usePurchaseOrderTransition() {
  const qc = useQueryClient();
  return useMutation<PurchaseOrder, Error, { id: string; action: PurchaseOrderTransitionAction }>({
    mutationFn: ({ id, action }) => PO_TRANSITIONS[action](id),
    onSuccess: (_data, { id }) => {
      qc.invalidateQueries({ queryKey: queryKeys.procurement.purchaseOrders() });
      qc.invalidateQueries({
        queryKey: queryKeys.procurement.purchaseOrderDetail(id),
      });
    },
  });
}

export function useRecordReceipt(poId: string) {
  const qc = useQueryClient();
  return useMutation<PurchaseOrder, Error, RecordReceiptInput>({
    mutationFn: (input) => {
      // Guard the empty-string fallback at the call boundary. The
      // receive page passes `poId ?? ''` when react-router has not
      // populated the param yet; without this guard the POST would hit
      // `.../purchase-orders//receive` and 404 with no signal about the
      // upstream cause. Fail-loud per EQD §3.
      if (!poId) {
        throw new Error('useRecordReceipt: poId is required');
      }
      return procurementAPI.recordReceipt(poId, input);
    },
    onSuccess: () => {
      // Invalidate this PO's detail + every PO list (status changed).
      qc.invalidateQueries({ queryKey: queryKeys.procurement.purchaseOrders() });
      qc.invalidateQueries({
        queryKey: queryKeys.procurement.purchaseOrderDetail(poId),
      });
    },
  });
}
